import { Navigate, Outlet, useParams } from "react-router-dom";
import { LayoutDashboard, Users, UserRoundCog, Boxes, ScrollText, Settings, HeartPulse, Puzzle, ShieldOff, Ban, CalendarDays, Stethoscope, Pill, FlaskConical, FileText, Contact, Tags, Receipt, Wallet, ShieldCheck, ShoppingCart, Package, BookOpen, Banknote, LineChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Shell } from "@/components/app/Shell";
import { EmptyState, FullPageLoader } from "@/components/app/kit";
import { useAuth, homeFor } from "@/context/AuthContext";
import { t } from "@/lib/i18n";

const buildSections = (me) => {
  const can = (p) => me.permissions.includes(p);
  const mods = me.modules || [];
  const has = (c) => mods.some((m) => m.code === c);
  const work = [
    can("dashboard:read") && { to: "/cabinet", end: true, label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-tenant-dashboard" },
    has("patients") && can("patients:read") && { to: "/cabinet/patients", label: "Patients", icon: HeartPulse, testid: "nav-tenant-patients" },
    has("agenda") && can("agenda:read") && { to: "/cabinet/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-tenant-agenda" },
    has("consultations") && can("consultations:read") && { to: "/cabinet/consultations", label: "Consultations", icon: Stethoscope, testid: "nav-tenant-consultations" },
    has("prescriptions") && can("prescriptions:read") && { to: "/cabinet/ordonnances", label: "Ordonnances", icon: Pill, testid: "nav-tenant-prescriptions" },
    has("exams") && can("exams:read") && { to: "/cabinet/examens", label: "Examens", icon: FlaskConical, testid: "nav-tenant-exams" },
    has("documents") && can("documents:read") && { to: "/cabinet/documents", label: "Documents", icon: FileText, testid: "nav-tenant-documents" },
    ...mods.filter((m) => !m.implemented).map((m) => ({ to: `/cabinet/m/${m.code}`, label: m.name, icon: Puzzle, testid: `nav-tenant-module-${m.code}` })),
  ].filter(Boolean);
  const fin = [
    has("finance") && can("finance:read") && { to: "/cabinet/finances", label: "Tableau financier", icon: LineChart, testid: "nav-tenant-finance" },
    has("billing") && can("billing:read") && { to: "/cabinet/factures", label: "Facturation", icon: Receipt, testid: "nav-tenant-billing" },
    has("cash") && can("cash:read") && { to: "/cabinet/caisse", label: "Caisse et trésorerie", icon: Wallet, testid: "nav-tenant-cash" },
    has("insurance") && can("insurance:read") && { to: "/cabinet/assurances", label: "Tiers payants", icon: ShieldCheck, testid: "nav-tenant-insurance" },
    has("pricing") && can("pricing:read") && { to: "/cabinet/tarifs", label: "Catalogue et tarifs", icon: Tags, testid: "nav-tenant-pricing" },
    has("purchases") && can("purchases:read") && { to: "/cabinet/achats", label: "Achats et dépenses", icon: ShoppingCart, testid: "nav-tenant-purchases" },
    has("inventory") && can("inventory:read") && { to: "/cabinet/stocks", label: "Stocks", icon: Package, testid: "nav-tenant-inventory" },
    has("accounting") && can("accounting:read") && { to: "/cabinet/comptabilite", label: "Comptabilité", icon: BookOpen, testid: "nav-tenant-accounting" },
    has("hr") && can("hr:read") && { to: "/cabinet/remunerations", label: "Rémunérations", icon: Banknote, testid: "nav-tenant-hr" },
  ].filter(Boolean);
  const admin = [
    can("staff:read") && { to: "/cabinet/personnel", label: "Personnel", icon: Contact, testid: "nav-tenant-staff" },
    can("users:read") && { to: "/cabinet/utilisateurs", label: "Utilisateurs", icon: Users, testid: "nav-tenant-users" },
    can("users:read") && { to: "/cabinet/roles", label: "Rôles et permissions", icon: UserRoundCog, testid: "nav-tenant-roles" },
    { to: "/cabinet/modules", label: "Modules du cabinet", icon: Boxes, testid: "nav-tenant-modules" },
    can("audit:read") && { to: "/cabinet/audit", label: "Journal d'audit", icon: ScrollText, testid: "nav-tenant-audit" },
    can("settings:read") && { to: "/cabinet/parametres", label: "Paramètres", icon: Settings, testid: "nav-tenant-settings" },
    can("settings:read") && { to: "/cabinet/abonnement", label: "Abonnement et préférences", icon: Tags, testid: "nav-tenant-subscription" },
  ].filter(Boolean);
  return [{ label: "Activité", items: work }, { label: "Gestion et finances", items: fin }, { label: "Administration", items: admin }].filter((s) => s.items.length);
};

const Blocked = ({ me, logout }) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm max-w-md w-full" data-testid="tenant-blocked">
      <EmptyState icon={Ban} title={`Accès au cabinet ${t(`status.${me.tenant.status}`).toLowerCase()}`}
        text="L'abonnement de ce cabinet est suspendu ou expiré. Vos données sont conservées. Contactez l'administration de la plateforme."
        action={<Button variant="outline" onClick={logout} data-testid="blocked-logout">Se déconnecter</Button>} />
    </div>
  </div>
);

export default function TenantLayout() {
  const { me, logout } = useAuth();
  if (me === null) return <FullPageLoader />;
  if (!me) return <Navigate to="/connexion" replace />;
  if (me.space !== "tenant") return <Navigate to={homeFor(me)} replace />;
  if (me.blocked) return <Blocked me={me} logout={logout} />;
  return (
    <Shell sections={buildSections(me)} spaceLabel="Espace cabinet" roleLabel={me.role?.name}
      brand={{ name: me.tenant.commercial_name || me.tenant.name, logo: me.tenant.logo_data }}>
      <Outlet />
    </Shell>
  );
}

export const ModuleGate = ({ module, perm, children }) => {
  const { hasModule, can } = useAuth();
  if (module && !hasModule(module))
    return <EmptyState icon={ShieldOff} testid="module-blocked" title="Module non activé" text="Ce module n'est pas autorisé pour votre cabinet. Contactez l'administration de la plateforme pour l'activer." />;
  if (perm && !can(perm))
    return <EmptyState icon={ShieldOff} testid="permission-blocked" title="Accès refusé" text="Votre rôle ne dispose pas de la permission nécessaire pour cette page." />;
  return children;
};

export const ModuleRoute = () => {
  const { code } = useParams();
  return <ModuleGate module={code}><Outlet /></ModuleGate>;
};
